(() => {
  if (customElements.get('amani-reviews')) return;

  const reviewWord = (n) => (n === 1 ? 'review' : 'reviews');
  const oneDecimal = (n) => (Math.round(Number(n) * 10) / 10).toFixed(1).replace(/\.0$/, '');
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  };
  const ORDER_HELP = 'That order number doesn’t look right. It’s in your order email, like #1187.';

  class AmaniReviews extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.form = this.querySelector('[data-form]');
      this.writeButton = this.querySelector('[data-write]');
      this.message = this.querySelector('[data-msg]');
      this.rate = this.querySelector('[data-rate]');
      this.showAll = false;
      this.writeButton?.addEventListener('click', () => this.toggleForm(this.form.hidden), options);
      this.querySelector('[data-cancel]')?.addEventListener('click', () => this.toggleForm(false), options);
      this.form?.addEventListener('submit', (event) => this.submit(event), options);
      const clearError = () => {
        if (this.message?.classList.contains('is-error')) this.say('');
      };
      this.form?.addEventListener('input', clearError, options);
      this.form?.addEventListener('change', clearError, options);
      this.querySelector('[data-more]')?.addEventListener('click', () => {
        this.showAll = true;
        this.renderList();
      }, options);
      if (this.rate) {
        const show = (value) => {
          this.rate.dataset.show = value || this.rate.querySelector('input:checked')?.value || '';
        };
        this.rate.addEventListener('change', () => show(), options);
        this.rate.querySelectorAll('label').forEach((label, index) => {
          label.addEventListener('mouseenter', () => show(String(index + 1)), options);
          label.addEventListener('mouseleave', () => show(), options);
        });
      }
      this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async load() {
      const api = this.dataset.api;
      const id = this.dataset.productId;
      if (!api || !id) return;
      try {
        const url = new URL(api);
        url.searchParams.set('reviews', id);
        const response = await fetch(url, { signal: this.controller.signal, credentials: 'omit', cache: 'no-store' });
        const data = await response.json();
        if (!data || !data.ok) throw new Error('reviews');
        this.data = data;
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.data = { count: 0, reviews: [], average: null, failed: true };
      }
      this.render();
    }

    render() {
      const data = this.data || { count: 0, reviews: [] };
      const score = this.querySelector('[data-score]');
      const empty = this.querySelector('[data-empty]');
      if (data.count > 0) {
        score.hidden = false;
        score.querySelector('.amr-stars').style.setProperty('--amr-rating', data.average);
        score.querySelector('[data-score-text]').textContent =
          `${oneDecimal(data.average)} out of 5 · ${data.count} ${reviewWord(data.count)}`;
        empty.hidden = true;
      } else {
        score.hidden = true;
        empty.hidden = Boolean(data.failed);
      }
      this.renderList();
      this.renderSummaries(data);
      this.addStructuredData(data);
    }

    renderList() {
      const list = this.querySelector('[data-list]');
      const more = this.querySelector('[data-more]');
      const all = (this.data && this.data.reviews) || [];
      const visible = this.showAll ? all : all.slice(0, 5);
      list.replaceChildren(...visible.map((review) => this.reviewItem(review)));
      more.hidden = all.length <= visible.length;
      if (!more.hidden) more.textContent = `Show all ${all.length} reviews`;
    }

    reviewItem(review) {
      const item = make('li', 'amr__item');
      const top = make('div', 'amr__item-top');
      const stars = make('span', 'amr-stars');
      stars.style.setProperty('--amr-rating', review.stars);
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${review.stars} out of 5 stars`);
      top.append(stars, make('span', 'amr__date', review.date));
      item.append(
        top,
        make('p', 'amr__text', review.text),
        make('p', 'amr__meta', [review.name, review.colour, review.fit].filter(Boolean).join(' · '))
      );
      return item;
    }

    renderSummaries(data) {
      document.querySelectorAll('[data-amr-summary]').forEach((link) => {
        if (!(data.count > 0)) {
          link.hidden = true;
          return;
        }
        link.hidden = false;
        link.querySelector('.amr-stars')?.style.setProperty('--amr-rating', data.average);
        const text = link.querySelector('[data-amr-summary-text]');
        if (text) text.textContent = `${oneDecimal(data.average)} · ${data.count} ${reviewWord(data.count)}`;
        link.setAttribute(
          'aria-label',
          `Rated ${oneDecimal(data.average)} out of 5 from ${data.count} ${reviewWord(data.count)}. Read the reviews.`
        );
      });
    }

    addStructuredData(data) {
      if (!(data.count > 0)) return;
      const rating = {
        '@type': 'AggregateRating',
        ratingValue: data.average,
        reviewCount: data.count,
        bestRating: 5,
        worstRating: 1,
      };
      const reviews = data.reviews.slice(0, 10).map((review) => ({
        '@type': 'Review',
        reviewRating: { '@type': 'Rating', ratingValue: review.stars, bestRating: 5, worstRating: 1 },
        author: { '@type': 'Person', name: review.name },
        datePublished: review.day,
        reviewBody: review.text,
      }));
      for (const script of document.querySelectorAll('script[type="application/ld+json"]')) {
        try {
          const json = JSON.parse(script.textContent);
          if (json && (json['@type'] === 'Product' || json['@type'] === 'ProductGroup')) {
            json.aggregateRating = rating;
            json.review = reviews;
            script.textContent = JSON.stringify(json);
            return;
          }
        } catch (error) {
          /* not ours to fix */
        }
      }
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: this.dataset.product,
        url: this.dataset.url,
        image: this.dataset.image || undefined,
        aggregateRating: rating,
        review: reviews,
      });
      document.head.append(script);
    }

    toggleForm(open) {
      if (!this.form) return;
      this.form.hidden = !open;
      this.writeButton.setAttribute('aria-expanded', String(open));
      if (open) {
        this.openedAt = Date.now();
        this.form.querySelector('input, select, textarea')?.focus();
      } else {
        this.say('');
        this.writeButton.focus();
      }
    }

    say(text, isError) {
      this.message.textContent = text;
      this.message.classList.toggle('is-error', Boolean(isError));
    }

    async submit(event) {
      event.preventDefault();
      const form = this.form;
      const value = (name) => String(form.elements[name]?.value || '').trim();
      const stars = Number(form.querySelector('input[name="stars"]:checked')?.value || 0);
      const colour = form.elements.colour;
      const fit = form.querySelector('input[name="fit"]:checked')?.value || '';
      const text = value('text');
      const name = value('name');
      const order = value('order');
      const stop = (message, field) => {
        this.say(message, true);
        field?.focus();
      };
      if (!stars) return stop('Pick a star rating.', form.querySelector('input[name="stars"]'));
      if (colour && !colour.value) return stop('Choose the colour you bought.', colour);
      if (text.length < 10) return stop('Tell us a little more. At least 10 characters.', form.elements.text);
      if (!name) return stop('Add your first name.', form.elements.name);
      if (order && !/^#?\d{3,7}$/.test(order)) return stop(ORDER_HELP, form.elements.order);

      const send = form.querySelector('[data-send]');
      send.disabled = true;
      this.say('Sending…');
      const review = {
        productId: this.dataset.productId,
        product: this.dataset.product,
        stars,
        colour: colour ? colour.value : '',
        fit,
        text,
        name,
        order,
        website: value('website'),
        opened: Date.now() - (this.openedAt || Date.now()),
      };
      try {
        const response = await fetch(this.dataset.api, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify({ action: 'reviewSubmit', review }),
          credentials: 'omit',
          signal: this.controller.signal,
        });
        const data = await response.json();
        if (!data || !data.ok) throw Object.assign(new Error('review'), { code: data && data.error });
        form.hidden = true;
        this.writeButton.hidden = true;
        this.querySelector('[data-empty]').hidden = true;
        const thanks = this.querySelector('[data-thanks]');
        thanks.hidden = false;
        thanks.focus();
      } catch (error) {
        if (error.name === 'AbortError') return;
        send.disabled = false;
        const code = error.code;
        this.say(
          code === 'order'
            ? ORDER_HELP
            : code === 'busy'
              ? 'Lots of reviews are coming in right now. Try again in a few minutes.'
              : code === 'bad'
                ? 'Something in that wasn’t right. Check it and try again.'
                : 'That didn’t send. Check your connection and try again.',
          true
        );
      }
    }
  }

  customElements.define('amani-reviews', AmaniReviews);
})();